import { type FC } from "react";
import { ConsiderationNumInformation } from "@userFrontend/features/publication/components/ConsiderationNumInformation";
import { type ButtonContentsProps } from "@legacy_user_frontend/components/projects/button_contents/src/button_contents_props";
import { LineConsultButton } from "@userFrontend/components/Buttons/LineConsultButton";
import { PopOver } from "@root/shared/components/PopOver";
import { KeepButtonContent } from "./src/keep_button_content";
import { ScoutButton } from "./src/scout_button";
import { TelButton } from "./src/tel_button";
import { FixButton } from "./src/fix_button";
import { TopContents } from "./src/top_contents";
import * as styles from "./styles.css";
import {
  contentsOuter,
  contentsOuterIs1Column,
  contents,
  buttonGroup,
  buttonGroupIsBlock,
  bottomContent,
  entriedButton,
  bottomContents,
  workCodeText,
  entryButton,
  titleText,
  awardsLabels,
  awardsLabel,
  salonTourButton,
} from "./src/styles.css";
import { InquiryMessageLabel } from "./src/inquiry_message_label";

type Props = {
  btnPositionControllingClassName: string;
  handleSalonTourButtonClick: () => void;
  handleLineConsultButtonClick: () => void;
  isSalonTourButtonVisible: boolean;
  isTelButtonVisible: boolean;
  scoutGaLabel: string;
  awardsLabelContent?: {
    awardsLabelRank?: string | undefined;
    awardsLabelText?: string | undefined;
  };
  dataStyleColumn: 1 | 2;
  isShowInquiryMessageLabel: boolean;
} & Omit<
  ButtonContentsProps,
  | "awardsLabel"
  | "awards2022Label"
  | "awards2023Label"
  | "awards2024Label"
  | "awards2025Label"
  | "awards2026Label"
>;

export const Presentation: FC<Props> = ({
  pageType,
  capitalizePageType,
  position,
  workCode,
  queryStr,
  isSp,
  isApp,
  isBiyo,
  isWebview,
  osType,
  clientShopId,
  gaLabel,
  title,
  imgSrc,
  dummyImgPath,
  isPreview,
  isContractRunning,
  isShowEntrySupplementPopover,
  considerationContents,
  buttonContents: {
    keepButtonContent,
    scoutButtonContent,
    entryButtonContent,
    salonTourButtonContent,
    lineConsultButtonContent,
  },
  telNumberContent,
  awardsLabelContent,
  salaryContents,
  images,
  albumCategoryItems,
  btnPositionControllingClassName,
  handleSalonTourButtonClick,
  handleLineConsultButtonClick,
  isSalonTourButtonVisible,
  isTelButtonVisible,
  scoutGaLabel,
  dataStyleColumn,
  isShowInquiryMessageLabel,
}) => {
  const is1column =
    !workCode || !entryButtonContent.isEnterable || (pageType !== "detail" && !imgSrc);

  return (
    <>
      <div
        className={`${contentsOuter} ${is1column ? contentsOuterIs1Column : ""}`}
        data-position={position}
      >
        {position === "top" && (
          <TopContents
            isSp={isSp}
            isBiyo={isBiyo}
            isWebview={isWebview}
            osType={osType}
            pageType={pageType}
            images={images}
            albumCategoryItems={albumCategoryItems}
            imgSrc={imgSrc}
            dummyImgPath={dummyImgPath}
            awardsLabel={awardsLabelContent}
            considerationContents={considerationContents}
            salaryContents={salaryContents}
            title={title}
            workCode={workCode}
            isEnterable={entryButtonContent.isEnterable}
            isContractRunning={isContractRunning}
          />
        )}
        {!workCode && <span className="js-footer-fix__first-point" />}
        {workCode && isContractRunning && (
          <div
            className={`${contents} ${btnPositionControllingClassName}`}
            data-style-column={dataStyleColumn}
            data-position={position}
          >
            <div
              className={`${buttonGroup} ${scoutButtonContent.isScouted ? buttonGroupIsBlock : ""}`}
              data-style-column={dataStyleColumn}
            >
              {entryButtonContent.hasEntried && (
                <div className={entriedButton} data-style-column={dataStyleColumn}>
                  応募済み
                </div>
              )}
              {entryButtonContent.isEnterable &&
                !entryButtonContent.hasEntried &&
                !scoutButtonContent.isScouted && (
                  <>
                    <div className={styles.entryButtonWrapper}>
                      {isShowEntrySupplementPopover && (
                        <div
                          className={styles.entryButtonPopoverContainer}
                          style={{ marginBottom: isSp ? "40px" : "50px" }}
                        >
                          <PopOver arrowPosition="bottomCenter">
                            <p className={styles.entryButtonPopOverSubText}>
                              書いていないこと・聞きたいことがあったら...
                            </p>
                            <p
                              className={styles.entryButtonPopOverText}
                              style={isSp ? { fontSize: "12px" } : {}}
                            >
                              質問メッセージも送れます
                            </p>
                          </PopOver>
                        </div>
                      )}
                      {/* FIXME: isPreview時にjavascript:void(0)を使用しているためanchor-is-validに違反。button要素への変更を検討 */}
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                      <a
                        /* eslint no-script-url: 0 */
                        href={isPreview ? "javascript:void(0)" : entryButtonContent.entryUrl}
                        className={`${entryButton({ isFixed: false })} ga-click appier-click firebase-click disable-on-preview js-add-query-strings`}
                        data-ga-action={`${pageType}_entry-${position}`}
                        data-ga-label={gaLabel}
                        data-appier-action={`trackTapJob${capitalizePageType}Entry`}
                        data-firebase-action="entry"
                        data-firebase-view-type="screen_view"
                        data-query-str={queryStr}
                        data-style-column={dataStyleColumn}
                      >
                        {isShowInquiryMessageLabel && <InquiryMessageLabel />}
                        応募画面へ進む
                      </a>
                    </div>
                    <KeepButtonContent
                      keepButtonContent={keepButtonContent}
                      workCode={workCode}
                      position={position}
                      gaLabel={gaLabel}
                      isPreview={isPreview}
                      pageType={pageType}
                      dataStyleColumn={dataStyleColumn}
                    />
                  </>
                )}
              {scoutButtonContent.isScouted && (
                <ScoutButton
                  scoutButtonContent={scoutButtonContent}
                  salonTourButtonContent={salonTourButtonContent}
                  queryStr={queryStr}
                  scoutGaLabel={scoutGaLabel}
                  hasEntried={entryButtonContent.hasEntried}
                  workCode={workCode}
                  position={position}
                  isFixedButton={false}
                  pageType={pageType}
                  isApp={isApp}
                  isPreview={isPreview}
                  dataStyleColumn={dataStyleColumn}
                  isShowInquiryMessageLabel={isShowInquiryMessageLabel}
                />
              )}
            </div>
            {!scoutButtonContent.isScouted && (
              <div
                className={bottomContents({
                  addMargin:
                    telNumberContent.isShowTellButton &&
                    !lineConsultButtonContent.isShowLineConsultButton,
                })}
                data-style-column={dataStyleColumn}
              >
                {isSp &&
                  telNumberContent.isShowTellButton &&
                  entryButtonContent.isEnterable &&
                  !entryButtonContent.hasEntried && (
                    <div
                      className={bottomContent({
                        is2column:
                          !scoutButtonContent.isScouted &&
                          salonTourButtonContent.isSalonTour &&
                          !entryButtonContent.hasEntried,
                      })}
                    >
                      <TelButton
                        telNumber={telNumberContent.telNumber}
                        position={position}
                        clientShopId={clientShopId}
                        gaLabel={gaLabel}
                        pageType={pageType}
                        capitalizePageType={capitalizePageType}
                        workCode={workCode}
                        isPreview={isPreview}
                      />
                      <div className={workCodeText}>
                        求人番号: <b>{workCode}</b>
                      </div>
                    </div>
                  )}
                {isSalonTourButtonVisible && (
                  <div
                    className={bottomContent({
                      is2column: telNumberContent.isShowTellButton && isSp,
                    })}
                  >
                    <button
                      type="button"
                      className={`${salonTourButton} ga-click`}
                      data-ga-action={`${pageType}_entry_tour-${position}`}
                      data-ga-label={gaLabel}
                      data-query-str={queryStr}
                      onClick={handleSalonTourButtonClick}
                    >
                      <i className="icon-kengaku" />
                      見学を希望する
                    </button>
                  </div>
                )}
              </div>
            )}
            {lineConsultButtonContent.isShowLineConsultButton && !entryButtonContent.hasEntried && (
              <div
                style={{
                  marginTop: isSalonTourButtonVisible || isTelButtonVisible ? "12px" : "0px",
                }}
              >
                <LineConsultButton onClick={handleLineConsultButtonClick} />
              </div>
            )}
          </div>
        )}
      </div>
      {!isSp && position === "top" && (
        <>
          {workCode && pageType === "detail" && (
            <>
              {awardsLabelContent && (
                <div className={awardsLabels}>
                  <span className={awardsLabel} data-rank={awardsLabelContent.awardsLabelRank}>
                    {awardsLabelContent.awardsLabelText}
                  </span>
                </div>
              )}
              {considerationContents.considerationDisplayFlag && entryButtonContent.isEnterable && (
                // !isSpの場合(PC時想定)に表示される
                <ConsiderationNumInformation
                  considerationNumText={considerationContents.considerationNumText}
                />
              )}
              <p className={titleText}>{title}</p>
            </>
          )}
        </>
      )}
      {position === "top" &&
        isSp &&
        !entryButtonContent.hasEntried &&
        !isPreview &&
        isContractRunning &&
        entryButtonContent.isEnterable && (
          <FixButton
            scoutButtonContent={scoutButtonContent}
            entryButtonContent={entryButtonContent}
            telNumberContent={telNumberContent}
            gaLabel={gaLabel}
            queryStr={queryStr}
            workCode={workCode}
            scoutGaLabel={scoutGaLabel}
            clientShopId={clientShopId}
            pageType={pageType}
            capitalizePageType={capitalizePageType}
            isApp={isApp}
            dataStyleColumn={dataStyleColumn}
            isPreview={isPreview}
          />
        )}
    </>
  );
};
